import React from 'react'
import { THEME_KEY } from '../utils/constants/localStorage.constants'
import { useStoredState } from '../hooks/useStoredState'

interface DarkModeContextType {
  darkMode: boolean
  toggleDarkMode: () => void
}

const DarkModeContext = React.createContext<DarkModeContextType>({
  darkMode: false,
  toggleDarkMode: () => {},
})

interface DarkModeProviderProps {
  children: React.ReactNode
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useStoredState({
    key: THEME_KEY,
    defaultValue: '', // empty means "no explicit user choice"
  })

  const systemPrefersDark =
    typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false

  const darkMode = theme ? theme === 'dark' : systemPrefersDark

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode)
    document.documentElement.style.setProperty('color-scheme', darkMode ? 'dark' : 'light')
  }, [darkMode])

  const toggleDarkMode = React.useCallback(() => {
    setTheme((prev: string) => (prev === 'dark' ? 'light' : 'dark'))
  }, [setTheme])

  const value = React.useMemo(() => ({ darkMode, toggleDarkMode }), [darkMode, toggleDarkMode])

  return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>
}

export const useDarkMode = () => React.useContext(DarkModeContext)
